// src/components/BlogPage.jsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './BlogPage.css';

// Blog posts data structure - easy to add more
const blogPosts = [
  {
    id: 1,
    title: "Pay Subcontractors Faster and Reduce Admin",
    excerpt: "Stop chasing timesheets and manual calculations. Learn how to streamline subcontractor payments and save hours every week.",
    date: "2026",
    readTime: "",
    image: "/images/blog-fast-payments.jpg",
    category: "Payments",
    content: `
      <h2>Stop chasing timesheets and manual calculations</h2>
      <p>Paying subcontractors should not mean chasing timesheets, checking bank details, calculating CIS deductions and answering payment queries every Friday.</p>
      
      <p>For many construction businesses, subcontractor payroll is still managed with spreadsheets, paper notes and manual calculations. That slows down payments, creates errors and wastes admin time.</p>
      
      <h2>A faster way to pay</h2>
      <p>PayCIS helps contractors pay subcontractors faster with a clear digital workflow. Subcontractors submit payment requests online, managers approve them in one place, and CIS deductions are calculated automatically.</p>
      
      <p>Instead of rebuilding each payment run from scratch, you get a faster, cleaner subcontractor payment process.</p>
      
      <h2>The result</h2>
      <ul>
        <li>Less payroll admin</li>
        <li>Fewer payment errors</li>
        <li>Smoother weekly subcontractor payments</li>
        <li>Better cashflow control</li>
      </ul>
      
      <p>If you want to save time and improve cashflow control, construction payroll software needs to remove friction from payday.</p>
    `
  },
  {
    id: 2,
    title: "How to Avoid CIS Mistakes",
    excerpt: "Manual processes lead to HMRC penalties. Discover how to reduce CIS errors and stay compliant.",
    date: "2026",
    readTime: "",
    image: "/images/blog-cis-mistakes.jpg",
    category: "CIS Compliance",
    content: `
      <h2>Why CIS mistakes happen</h2>
      <p>CIS compliance often breaks down because too much is handled manually. A missed subcontractor verification, a wrong tax deduction, poor payment records or an incorrect CIS return can lead to HMRC penalties, delays and avoidable admin.</p>
      
      <p>Many contractors still manage CIS across separate systems for timesheets, payments and records. That creates unnecessary re-keying and increases the risk of human error.</p>
      
      <h2>How PayCIS helps</h2>
      <p>PayCIS helps reduce CIS mistakes by keeping subcontractor verification, deduction calculations, payment records and CIS return preparation in one system.</p>
      
      <ul>
        <li>Clearer audit trail</li>
        <li>Fewer missed steps</li>
        <li>Better CIS compliance</li>
        <li>Reduced HMRC risk</li>
      </ul>
      
      <p>Good CIS software is not just about saving time. It helps contractors reduce HMRC risk, improve accuracy and stay in control of subcontractor payroll.</p>
    `
  },
  {
    id: 3,
    title: "Stop Chasing Subcontractors for Information",
    excerpt: "Missing bank details and timesheets slow down payroll. Here's how to get what you need without the back-and-forth.",
    date: "2026",
    readTime: "",
    image: "/images/blog-chasing-info.jpg",
    category: "Efficiency",
    content: `
      <h2>The information problem</h2>
      <p>One of the biggest causes of payment delays is missing subcontractor information. Incorrect bank details, incomplete timesheets, missing tax numbers and last-minute changes all create extra admin and slow down payroll.</p>
      
      <p>This is a common problem in construction, where key information often comes in by text, phone call, email or photo. Someone then has to pull everything together manually and hope nothing has been missed.</p>
      
      <h2>The PayCIS solution</h2>
      <p>PayCIS reduces that admin by letting subcontractors register their own details, submit payment requests online and keep their records up to date.</p>
      
      <ul>
        <li>Fewer back-and-forth messages</li>
        <li>Better data accuracy</li>
        <li>Smoother payment process</li>
        <li>Less time chasing paperwork</li>
      </ul>
      
      <p>When subcontractor details are captured properly from the start, approvals are faster, payroll runs more smoothly and contractors spend less time chasing paperwork.</p>
    `
  },
  {
    id: 4,
    title: "How to Scale a Construction Business",
    excerpt: "A payroll process that works for five subcontractors often fails at fifty. Learn how to scale without breaking admin.",
    date: "2026",
    readTime: "read",
    image: "/images/blog-scale-business.jpg",
    category: "Business Growth",
    content: `
      <h2>The scaling challenge</h2>
      <p>A payroll process that works for five subcontractors often fails at fifty. As construction businesses grow, admin grows with them. More subcontractors means more registrations, more timesheets, more payment checks, more statements and more compliance risk.</p>
      
      <p>If that growth is still being managed manually, admin quickly becomes a bottleneck. Time gets lost, errors increase and the back office starts holding the business back.</p>
      
      <h2>Scaling with PayCIS</h2>
      <p>PayCIS helps construction firms scale by putting subcontractor onboarding, payment requests, CIS deductions, statements and returns into one cloud-based system.</p>
      
      <ul>
        <li>Structured process as headcount grows</li>
        <li>Reduced need for extra payroll admin</li>
        <li>Take on more workers and more jobs</li>
        <li>No admin drag on the business</li>
      </ul>
      
      <p>For growing contractors, the benefit is simple. You can take on more workers and more jobs without letting subcontractor payroll become a drag on the business.</p>
    `
  },
  {
    id: 5,
    title: "Reduce Subcontractor Payment Queries",
    excerpt: "Stop answering the same questions every week. Give subcontractors self-service access to payment information.",
    date: "2026",
    readTime: "read",
    image: "/images/blog-payment-queries.jpg",
    category: "Efficiency",
    content: `
      <h2>The query problem</h2>
      <p>A lot of payroll admin happens after payment is made. Subcontractors ask what they were paid, what tax was deducted, whether materials were included, or for another copy of their statement. Those queries add up and waste time.</p>
      
      <p>The problem is usually poor access to information. If subcontractors cannot view their payment history or download payment statements themselves, every request comes back to the office.</p>
      
      <h2>The PayCIS solution</h2>
      <p>PayCIS solves this by giving subcontractors online access to their payment information and statements. They can check their records themselves instead of calling or emailing for copies.</p>
      
      <ul>
        <li>Reduces repetitive admin</li>
        <li>Cuts payment-related queries</li>
        <li>Less disruption for contractors</li>
        <li>Faster access for subcontractors</li>
      </ul>
      
      <p>For contractors, that means less disruption, fewer document requests and a more efficient subcontractor payroll process. For subcontractors, it means faster access to the information they need.</p>
    `
  },
  {
    id: 6,
    title: "Protect Your Business From Bogus Claims",
    excerpt: "Without proper contracts, subcontractors may claim employee rights. Learn how proper onboarding protects your business.",
    date: "2026",
    readTime: "read",
    image: "/images/blog-protect-business.jpg",
    category: "Risk Management",
    content: `
      <h2>The risk of informal onboarding</h2>
      <p>Taking on subcontractors without a proper contract creates risk. If the relationship later turns sour, a worker may try to claim they were really an employee and demand holiday pay, sick pay or other employment rights.</p>
      
      <p>Without clear written terms, the contractor is in a weaker position. What started as informal onboarding can turn into a costly dispute, with time lost dealing with claims and paperwork.</p>
      
      <h2>How PayCIS protects you</h2>
      <p>PayCIS helps protect contractors by building proper onboarding into the process. Subcontractors go through a structured setup, agree to terms and enter into a contract for services from the outset.</p>
      
      <ul>
        <li>Stronger records from day one</li>
        <li>Less ambiguity about the relationship</li>
        <li>Better protection against bogus claims</li>
        <li>Reduced legal and commercial risk</li>
      </ul>
      
      <p>The benefit is stronger records, less ambiguity and better protection against bogus employment claims. Good subcontractor management is not just about faster admin. It is also about reducing legal and commercial risk.</p>
    `
  }
];

const BlogPage = () => {
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const featuredPost = blogPosts[0];
  const remainingPosts = blogPosts.slice(1);

  const openModal = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
    document.body.style.overflow = 'auto';
  };

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = "https://placehold.co/600x400/2a4252/white?text=PayCIS";
  };

  return (
    <>
<Helmet>
  <title>PayCIS Blog | CIS Payroll Insights for UK Contractors</title>

  <meta
    name="description"
    content="Read expert insights on CIS deductions, subcontractor payments, payroll automation, and construction industry best practices with PayCIS blog."
  />

  <meta
    name="keywords"
    content="CIS blog UK, contractor payroll insights, subcontractor payments guide, CIS deductions tips, construction payroll software blog, HMRC CIS updates, PayCIS articles"
  />

  <meta name="author" content="PayCIS" />

  {/* Open Graph */}
  <meta property="og:title" content="PayCIS Blog | CIS Payroll Insights" />
  <meta
    property="og:description"
    content="Expert articles on CIS compliance, subcontractor payments, and construction payroll automation."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://paycis.co.uk/blog" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="PayCIS Blog" />
  <meta
    name="twitter:description"
    content="Insights on CIS payroll, subcontractor payments, and construction industry automation."
  />

  <link rel="canonical" href="https://paycis.co.uk/blog" />
</Helmet>

      <div className="blog-page">
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="blog-paycis-container">
            <div className="blog-hero-content">
              {/* <div className="blog-hero-badge">Blog</div> */}
              <h1 className="blog-hero-title">
                PayCIS  <span className="highlight">Blog </span> 
              </h1>
              <p className="blog-hero-description">
                Insights into how the PayCIS software can help you run your construction business.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="blog-featured">
          <div className="blog-paycis-container">
            <div className="featured-wrapper" onClick={() => openModal(featuredPost)}>
              <div className="featured-image">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title}
                  onError={handleImageError}
                />
                {/* <span className="featured-category">{featuredPost.category}</span> */}
              </div>
              <div className="featured-content">
                <div className="featured-meta">
                  <span>{featuredPost.date}</span>
                  <span>•</span>
                  <span>{featuredPost.readTime}</span>
                </div>
                <h2 className="featured-title">{featuredPost.title}</h2>
                <p className="featured-excerpt">{featuredPost.excerpt}</p>
                <div className="featured-link">
                  Read Article →
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Grid - All Posts */}
        <section className="blog-grid-section">
          <div className="paycis-container">
            <div className="blog-grid">
              {remainingPosts.map((post) => (
                <article key={post.id} className="blog-card" onClick={() => openModal(post)}>
                  <div className="blog-card-image">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      onError={handleImageError}
                    />
                    {/* <span className="blog-card-category">{post.category}</span> */}
                  </div>
                  <div className="blog-card-content">
                    <div className="blog-card-meta">
                      <span>{post.date}</span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="blog-card-title">{post.title}</h3>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <div className="blog-card-link">
                      Read more →
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        {/* <section className="blog-newsletter">
          <div className="paycis-container">
            <div className="newsletter-wrapper">
              <div className="newsletter-content">
                <h3 className="newsletter-title">Get weekly insights</h3>
                <p className="newsletter-description">
                  Subscribe to our newsletter and never miss important CIS updates and tips.
                </p>
              </div>
              <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="newsletter-input"
                />
                <button type="submit" className="newsletter-btn">
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </section> */}

        {/* Article Modal */}
        {isModalOpen && selectedPost && (
          <div className="blog-modal-overlay" onClick={closeModal}>
            <div className="blog-modal-container" onClick={(e) => e.stopPropagation()}>
              <button className="blog-modal-close" onClick={closeModal}>×</button>
              
              <div className="blog-modal-header">
                <img 
                  src={selectedPost.image} 
                  alt={selectedPost.title}
                  onError={handleImageError}
                />
                <div className="blog-modal-header-content">
                  <span className="modal-category">{selectedPost.category}</span>
                  <h2>{selectedPost.title}</h2>
                  <div className="modal-meta">
                    <span>{selectedPost.date}</span>
                    <span className="meta-sep">•</span>
                    <span>{selectedPost.readTime}</span>
                  </div>
                </div>
              </div>
              
              <div className="blog-modal-body">
                <div dangerouslySetInnerHTML={{ __html: selectedPost.content }} />
              </div>
              
              <div className="blog-modal-footer">
                <button className="modal-close-btn" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BlogPage;